import { useState, useEffect } from 'react';
import { Modal, Select, Form, message } from 'antd';
import { ClientService } from '../../../../../shared/api/ClientService';
import API from '../../../../../utils/api';
import useLocale from '../../../../../hooks/useLocale';

interface IProps {
  fileId: string;
  submissionId: string;
  currentPeriodId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const MoveIncomeExpensePeriodModal = ({
  fileId,
  submissionId,
  currentPeriodId,
  isOpen,
  onClose,
  onSuccess,
}: IProps): JSX.Element => {
  const { t } = useLocale();
  const [form] = Form.useForm();

  const [pendingPeriods, setPendingPeriods] = useState<ClientService.IncomeExpenseSubmissionPeriodDto[]>([]);
  const [isLoadingPeriods, setIsLoadingPeriods] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !fileId) return;

    setIsLoadingPeriods(true);
    API.schedulesAll(fileId)
      .then((schedules) => {
        const pending = schedules.filter(
          (s: ClientService.IncomeExpenseSubmissionPeriodDto) =>
            s.status === ClientService.IncomeExpensePeriodStatusEnum.Pending &&
            s.externalIeSubmissionPeriodId !== currentPeriodId
        );
        setPendingPeriods(pending);
      })
      .catch(() => {
        message.error('Failed to load income & expense periods.');
      })
      .finally(() => setIsLoadingPeriods(false));
  }, [isOpen, fileId, currentPeriodId]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const result = await API.period(fileId, submissionId, {
        newExternalIeSubmissionPeriodId: values.newPeriodId,
      } as ClientService.MoveIncomeExpenseSubmissionPeriodDto);

      if (result?.result === ClientService.Result.Successful) {
        message.success('Income & Expense period updated successfully.');
        form.resetFields();
        onSuccess();
        onClose();
      } else {
        const errorMsg = result?.messages?.[0]?.body ?? 'Failed to update period.';
        message.error(errorMsg);
      }
    } catch {
      // form validation error — do nothing
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const periodOptions = pendingPeriods.map((p) => ({
    value: p.externalIeSubmissionPeriodId,
    label:
      p.period != null
        ? new Date(0, (p.period as number) - 1).toLocaleString('default', { month: 'short' })
        : p.externalIeSubmissionPeriodId,
  }));

  return (
    <Modal
      title="Edit Income & Expense Period"
      open={isOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Save"
      cancelText={t.CANCEL ?? 'Cancel'}
      confirmLoading={isSubmitting}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="newPeriodId"
          label="Select New Period"
          rules={[{ required: true, message: 'Please select a period.' }]}
        >
          <Select loading={isLoadingPeriods} placeholder="Select a pending period" options={periodOptions} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MoveIncomeExpensePeriodModal;
